import { CpuChipIcon, DocumentArrowUpIcon } from '@heroicons/react/24/solid'
import { RootState } from '@/redux/store'
import { useAppSelector } from '@/redux/hooks'
import { useState, useMemo, useCallback } from 'react'
import { Bot, Server, Thermometer, Database } from 'lucide-react'
import {
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from '../ui/sidebar'
import { cn } from '@/lib/utils'

export enum ActiveSection {
  BEHAVIOR = 'behavior',
  CONFIG = 'config',
  RAG_CONFIG = 'ragConfig',
  UPLOAD = 'upload',
  LLM_PREFERENCE = 'llmPreference',
  MCP_SERVER = 'MCPServer',
}

export default function ConfigSideBar() {
  const { openChatBoxes } = useAppSelector(
    (state: RootState) => state.testChatMessages
  )

  const [activeSection, setActiveSection] = useState<ActiveSection>(
    ActiveSection.BEHAVIOR
  )

  const sidebarOptions = useMemo(
    () => [
      {
        key: ActiveSection.BEHAVIOR,
        label: 'Assistant Behavior',
        icon: Bot,
      },
      {
        key: ActiveSection.CONFIG,
        label: 'Model Configuration',
        icon: Thermometer,
      },
      {
        key: ActiveSection.RAG_CONFIG,
        label: 'RAG Configuration',
        icon: Database,
      },
      {
        key: ActiveSection.UPLOAD,
        label: 'Upload Files',
        icon: DocumentArrowUpIcon,
      },
      {
        key: ActiveSection.LLM_PREFERENCE,
        label: 'LLM Preference',
        icon: CpuChipIcon,
      },
      {
        key: ActiveSection.MCP_SERVER,
        label: 'MCP Server',
        icon: Server,
      },
    ],
    []
  )

  const scrollToSection = useCallback((sectionId: ActiveSection) => {
    const element = document.getElementById(sectionId)
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      })
    }
  }, [])

  const sectionHandlers = useMemo(() => {
    const handlers: Record<ActiveSection, (e: React.MouseEvent<HTMLButtonElement>) => void> = {} as any
    sidebarOptions.forEach((option) => {
      handlers[option.key] = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.preventDefault()
        e.stopPropagation()
        if (openChatBoxes.length !== 0) return
        setActiveSection(option.key)
        scrollToSection(option.key)
      }
    })
    return handlers
  }, [sidebarOptions, openChatBoxes.length, scrollToSection])

  return (
    <div className='w-80 bg-gray-50 border-r border-gray-200'>
      <SidebarContent className='p-6 h-full'>
        <SidebarGroup>
          <SidebarGroupLabel className='text-xl font-bold text-gray-900 mb-6'>
            Configuration
          </SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu className='space-y-2'>
              {sidebarOptions.map((option) => {
                const IconComponent = option.icon
                return (
                  <SidebarMenuItem key={option.key}>
                    <SidebarMenuButton
                      onClick={sectionHandlers[option.key]}
                      className={cn(
                        'w-full text-left p-4 rounded-lg transition-colors flex items-start gap-3 h-auto min-h-[60px] border',
                        activeSection === option.key
                          ? 'bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-50'
                          : 'text-gray-700 hover:bg-gray-50 border-transparent hover:border-gray-200',
                        openChatBoxes.length !== 0 &&
                          'opacity-50 cursor-not-allowed'
                      )}
                      disabled={openChatBoxes.length !== 0}
                    >
                      <IconComponent className='w-5 h-5 mt-0.5 flex-shrink-0' />
                      <div className='flex-1 text-left'>
                        <div className='font-medium'>{option.label}</div>
                      </div>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                )
              })}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
    </div>
  )
}
